import React, {useEffect, useId} from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Button, Field, Input, MessageBar, MessageBarBody, Tooltip} from '@fluentui/react-components';
import { ProductImage } from '../Components/Helpers/ProductImage';
import { useGetProductQuery, useGetStorefrontQuery } from '../Api/Api';
import { ISearchContext } from '../Api/Models/Products';
import FullPageSpinner from '../Components/Spinner/FullPageSpinner';
import CurrencyFormat from '../Components/Helpers/CurrencyFormat';
import {FALLBACK_IMAGE} from "../Constants/FallbackImage";
import ProductVideoModal from '../Components/Product/ProductVideoModal';
import ProductImageModal from '../Components/Product/ProductImageModal';
import FavoriteButton from '../Components/Product/FavoriteButton';

import {BoxSearchFilled, ChevronLeftRegular, ChevronRightRegular, HomeFilled, PlayCircleFilled} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import CAN, {PRICING, CART_MANAGEMENT} from "../Components/Auth/Permissions";
import {useGetPreparedCartQuery} from "../Api/PreparedCartEndpoints";
import {useSelector} from "react-redux";
import {RootState} from "../Store/Store";
import {AddToCartButton} from "./SearchPage";
import BlockedCartNotice from '../Components/Cart/BlockedCartNotice';
import LinkedProducts from '../Components/Products/LinkedProducts';
import FrequentlyAddedTogether from '../Components/Products/FrequentlyAddedTogether';
import MoreLikeThis from '../Components/Products/MoreLikeThis';
import { DEFAULT_SELECTED_THUMB_STYLE, parseProductPageConfig } from '../Components/Helpers/productPageConfig';
import { elementStyleFrom } from '../Components/Helpers/searchPageConfig';
import { useSideScroll } from '../Components/Helpers/useSideScroll';
import CollapsibleDescription from '../Components/Product/CollapsibleDescription';
import { ProductAttributeChips, ProductTagChips } from '../Components/Product/ProductChips';
import QuotePrice from '../Components/Product/QuotePrice';
import PriceQuantityNotice from '../Components/Product/PriceQuantityNotice';
import PromoPrice from '../Components/Product/PromoPrice';
import StockAvailabilityNotice from '../Components/Product/StockAvailabilityNotice';
import { useLocalization } from '../Components/Helpers/useLocalization';

const ProductPage: React.FC = () => {
    const L = useLocalization();
    const canSeePricing = CAN(PRICING);
    const canAddToCart = CAN(CART_MANAGEMENT);
    const settings = useSelector((state: RootState) => state.cart);

    const { productId } = useParams<{ productId: string }>();
    // Interest signal: the surface (+ search criteria) the shopper navigated here from, carried as
    // router state by ProductCard/SearchPage's nav() calls. Undefined on a bare visit (bookmark,
    // refresh, typed URL) — the view is then recorded with no source, which is correct.
    const location = useLocation();
    const navState = (location.state ?? {}) as { source?: string; searchContext?: ISearchContext };
    const inputId = useId();
    const [quantity, setQuantity] = React.useState<number>(1);
    const [tmpQuantity, setTmpQuantity] = React.useState<string>('1');
    const product = useGetProductQuery({ productId: productId ?? '', source: navState.source, searchContext: navState.searchContext}, {skip: !productId});
    const sfSettings = useGetStorefrontQuery();
    // Merchant product-page styling (productPageConfig JSON). Each section applies to a bounding
    // component of the page as a plain inline style (these elements are not theme-forced).
    const productConfig = React.useMemo(() => parseProductPageConfig(sfSettings.data?.productPageConfig), [sfSettings.data?.productPageConfig]);
    const containerStyle = React.useMemo(() => elementStyleFrom(productConfig.container), [productConfig.container]);
    const titleStyle = React.useMemo(() => elementStyleFrom(productConfig.title, 'heading'), [productConfig.title]);
    const imageStyle = React.useMemo(() => elementStyleFrom(productConfig.image, 'image'), [productConfig.image]);
    const imageFrameStyle = React.useMemo(() => elementStyleFrom(productConfig.imageFrame), [productConfig.imageFrame]);
    const galleryStripStyle = React.useMemo(() => elementStyleFrom(productConfig.galleryStrip), [productConfig.galleryStrip]);
    const thumbStyle = React.useMemo(() => elementStyleFrom(productConfig.thumb), [productConfig.thumb]);
    // Merged OVER thumbStyle on the selected thumbnail. thumbStyle is an inline style, so a stylesheet
    // rule could not mark the selection on a storefront that styles its thumbnails — it has to win here.
    const selectedThumbStyle = React.useMemo(
        () => (productConfig.thumbSelected ? elementStyleFrom(productConfig.thumbSelected) : DEFAULT_SELECTED_THUMB_STYLE),
        [productConfig.thumbSelected]
    );
    const scrollButtonStyle = React.useMemo(() => elementStyleFrom(productConfig.scrollButton), [productConfig.scrollButton]);
    const tagsStyle = React.useMemo(() => elementStyleFrom(productConfig.tags), [productConfig.tags]);
    const attributesStyle = React.useMemo(() => elementStyleFrom(productConfig.attributes), [productConfig.attributes]);
    // Tags are decorations the storefront opts into; attributes are always shown when the product has them.
    const tagsEnabled = sfSettings.data?.enableProductTags ?? false;
    const priceBlockStyle = React.useMemo(() => elementStyleFrom(productConfig.priceBlock), [productConfig.priceBlock]);
    const promoBadgeStyle = React.useMemo(() => elementStyleFrom(productConfig.promoBadge), [productConfig.promoBadge]);
    const promoOriginalPriceStyle = React.useMemo(() => elementStyleFrom(productConfig.promoOriginalPrice), [productConfig.promoOriginalPrice]);
    const promoEndDateStyle = React.useMemo(() => elementStyleFrom(productConfig.promoEndDate), [productConfig.promoEndDate]);
    const msrpStyle = React.useMemo(() => elementStyleFrom(productConfig.msrp), [productConfig.msrp]);
    const quantityStyle = React.useMemo(() => elementStyleFrom(productConfig.quantity, 'input'), [productConfig.quantity]);
    const descriptionStyle = React.useMemo(() => elementStyleFrom(productConfig.description, 'body'), [productConfig.description]);
    const addToCartButtonStyle = React.useMemo(() => elementStyleFrom(productConfig.addToCartButton), [productConfig.addToCartButton]);
    const addToCartLabelStyle = React.useMemo(() => elementStyleFrom(productConfig.addToCartLabel, 'label'), [productConfig.addToCartLabel]);
    const [selectedImageIndex, setSelectedImageIndex] = React.useState<number>(0);
    const [imageModalOpen, setImageModalOpen] = React.useState<boolean>(false);
    // Same chevron behavior as a section's side-scrolling product row (see ProductScroller).
    const thumbScroll = useSideScroll('.product-thumb', 8, [product.data]);
    const [videoOpen, setVideoOpen] = React.useState<boolean>(false);
    const [warningMessage, setWarningMessage]= React.useState<string>('');
    const getPreparedCart = useGetPreparedCartQuery({cartId: settings.selectedCartId ?? ''}, {skip: !settings.selectedCartId});
    const [canActuallyAddToCart, setCanActuallyAddToCart] = React.useState(false);
    const [cartBlocked, setCartBlocked] = React.useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if(settings.selectedCartId && getPreparedCart.data) {
            const canAddToPreparedCart = getPreparedCart.data.canAddAdditionalItems ?? false;
            setCanActuallyAddToCart(canAddToPreparedCart && canAddToCart);
            setCartBlocked(!canAddToPreparedCart);
        }
        else{
            setCanActuallyAddToCart(canAddToCart);
            setCartBlocked(false);
        }
    }, [canAddToCart, getPreparedCart.data, settings.selectedCartId]);

    const trySetQuantity = (quantity: number) => {
        if(quantity <1) {
            setWarningMessage('Quantity must be at least 1');
            setQuantity(1);
        }
        else if(product?.data?.maxQuantity && quantity > product.data.maxQuantity) {
            setWarningMessage(`Only ${product.data.maxQuantity} available for purchase`)
            setQuantity(product.data.maxQuantity)
        }
        else{
            setWarningMessage('');
            setQuantity(quantity);
        }
    }
    
    React.useEffect(() => {
        setTmpQuantity(quantity.toString());
    }, [quantity]);

    if (product.isLoading) return <FullPageSpinner message={L('localization_common_loading')} />

    const blur = () => {
        let parsedQuantity = parseInt(tmpQuantity);
        if(parsedQuantity < 1) {parsedQuantity = 1}
        trySetQuantity(isNaN(parsedQuantity) ? 1 : parsedQuantity);
    }
    const stripNonNumeric = (value: string): string =>{
        return value.replace(/[^0-9]/g, '');
    }

    const trySetTmpQuantity = (value: string) => {
        const numericValue = stripNonNumeric(value);
        if(numericValue === '') {
            setWarningMessage('Quantity cannot be empty');
            setTmpQuantity('');
            return;
        }
        const parsedQuantity = parseInt(numericValue);
        if(isNaN(parsedQuantity)){
            setWarningMessage(`Quantity must be a number`);
            return;
        }
        else if(parsedQuantity < 1) {
            setWarningMessage(`Quantity must be a number greater than 0`);
            setTmpQuantity('1');
        } else {
            setWarningMessage('');
            setTmpQuantity(numericValue);
        }
    }

    // Build the ordered, de-duplicated set of images (primary first). The gallery is
    // only shown when the storefront enables it and there is more than one image.
    const galleryImages = Array.from(new Set(
        [product.data?.image, ...(product.data?.images ?? [])].filter((i): i is string => !!i && i.trim() !== '')
    ));
    const hasImages = galleryImages.length > 0;
    const images = hasImages ? galleryImages : [FALLBACK_IMAGE];
    const showGallery = (sfSettings.data?.enableProductImageGallery ?? false) && images.length > 1;
    const selectedImage = images[Math.min(selectedImageIndex, images.length - 1)];
    const videoUrl = (sfSettings.data?.allowProductVideoPlayback ?? false)
        ? (product.data?.videoUrls ?? []).find(v => !!v && v.trim() !== '')
        : undefined;

    return (<div className='sf-detail-page' style={{ width: '100%', maxWidth: '800px', margin: '0 auto', ...containerStyle }}>{product.data ?
        <div>
            <h2 style={{lineHeight: 1.3, ...titleStyle}}>{product.data?.name}</h2>
            <div className='product-gallery' style={imageStyle}>
                <div className={`product-gallery-main${showGallery ? ' fixed-ratio' : ''}`} style={imageFrameStyle}>
                    <ProductImage
                        src={selectedImage}
                        // No real image means the fallback placeholder is showing — nothing worth enlarging.
                        className={`product-image${hasImages ? ' product-image-zoomable' : ''}`}
                        fit='contain'
                        onClick={hasImages ? () => setImageModalOpen(true) : undefined}
                        />
                    {videoUrl && (
                        <Tooltip content="Play video" relationship="label">
                            <button
                                type="button"
                                aria-label="Play product video"
                                className='pc-video-play'
                                onClick={() => setVideoOpen(true)}
                            >
                                <PlayCircleFilled />
                            </button>
                        </Tooltip>
                    )}
                </div>
                {showGallery && (
                    <div className='card-scroller-wrap'>
                        {thumbScroll.canScrollLeft &&
                            <Button
                                className='card-scroller-arrow card-scroller-arrow-left'
                                shape='circular'
                                appearance='subtle'
                                icon={<ChevronLeftRegular />}
                                aria-label='Scroll left'
                                style={{ background: 'rgba(255,255,255,0.9)', ...scrollButtonStyle }}
                                onClick={() => thumbScroll.scrollByItem('left')}
                            />
                        }
                        <div
                            className='product-thumbs'
                            ref={thumbScroll.ref}
                            onScroll={thumbScroll.checkScroll}
                            style={galleryStripStyle}
                        >
                            {images.map((img, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    className={`product-thumb${i === selectedImageIndex ? ' selected' : ''}`}
                                    aria-label={`View image ${i + 1}`}
                                    aria-pressed={i === selectedImageIndex}
                                    onClick={() => setSelectedImageIndex(i)}
                                    style={i === selectedImageIndex ? { ...thumbStyle, ...selectedThumbStyle } : thumbStyle}
                                >
                                    <ProductImage src={img} alt="" />
                                </button>
                            ))}
                        </div>
                        {thumbScroll.canScrollRight &&
                            <Button
                                className='card-scroller-arrow card-scroller-arrow-right'
                                shape='circular'
                                appearance='subtle'
                                icon={<ChevronRightRegular />}
                                aria-label='Scroll right'
                                style={{ background: 'rgba(255,255,255,0.9)', ...scrollButtonStyle }}
                                onClick={() => thumbScroll.scrollByItem('right')}
                            />
                        }
                    </div>
                )}
            </div>
            <div className='product-data'>
                {tagsEnabled && <ProductTagChips tags={product.data?.tags} style={tagsStyle} />}
                {product.data?.manufacturerPartNumber && <p className='product-mfp' style={{fontStyle: 'italic'}}>Part Number: {product.data.manufacturerPartNumber}</p>}
                <ProductAttributeChips
                    attributes={product.data?.attributes}
                    style={attributesStyle}
                    taxonomy={{
                        segment: product.data?.segment,
                        category: product.data?.category,
                        subCategory: product.data?.subCategory,
                    }}
                />
                {product.data?.hidePrice
                    ? <QuotePrice
                        as='h2'
                        className='product-price product-quote-price'
                        style={priceBlockStyle}
                        itemId={product.data.id}
                        itemName={product.data.name}
                        quantity={quantity}
                    />
                    : product.data?.price && canSeePricing && <h2 className='product-price' style={priceBlockStyle}><CurrencyFormat value={product.data.price} /></h2>}
                {/* Ordered price, promo, then list price. The promo block explains what the price
                    currently IS, so it belongs directly under it; the list price is the manufacturer's
                    number and is the least specific of the three. With list price in the middle, two
                    struck-through amounts sat adjacent and read as one confused pair. */}
                <PromoPrice
                    product={product.data}
                    canSeePricing={canSeePricing}
                    className='product-promo'
                    style={{ margin: '0.25em 0 0' }}
                    badgeStyle={promoBadgeStyle}
                    originalPriceStyle={promoOriginalPriceStyle}
                    endDateStyle={promoEndDateStyle}
                />
                {(canSeePricing && product.data?.msrp && product.data?.price && product.data.msrp > product.data.price) &&
                    <p className='product-msrp' style={msrpStyle}>List Price: <span className='msrp-value'><CurrencyFormat value={product.data.msrp} /></span></p>
                }
                {/* Qualifies the price, so it sits with it rather than below the button — and the
                    quantity ceiling has to be readable before the quantity field, not after it. */}
                <PriceQuantityNotice product={product.data} canSeePricing={canSeePricing} className='product-price-ceiling' style={{ margin: '0.25em 0 0' }} />
                <StockAvailabilityNotice product={product.data} className='product-limited' style={{ margin: '0.25em 0 0' }} />
                {canActuallyAddToCart &&
                <Field style={{marginBottom: '1em', marginTop: '1em'}}>
                    <Input
                        type='number'
                        contentBefore={<>Quantity: </>}
                        style={{width: '100%', textAlign: 'right', ...quantityStyle}}
                        id={inputId}
                        onBlur={blur}
                        value={tmpQuantity}
                        onChange={(_,d) => trySetTmpQuantity(d.value)
                    }
                    />
                </Field>
                }
                {warningMessage && 
                <MessageBar intent = {"warning"}>
                    <MessageBarBody>
                        {warningMessage}
                    </MessageBarBody>
                </MessageBar>
                }
                {/* Heart sits to the LEFT of Add to Cart, on the same line. The heart renders even when
                    add-to-cart doesn't (e.g. a quote-only item), so the row owns the spacing. */}
                {product.data && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75em', marginTop: '1em' }}>
                        <FavoriteButton product={product.data} source="product" />
                        {canActuallyAddToCart && (
                            <div
                                onClick={e => e.stopPropagation()}
                                className="card-add-to-cart"
                                style={{
                                    zIndex: 3,
                                    background: 'white',
                                    borderRadius: 13,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                                    padding: 0,
                                    cursor: 'pointer',
                                    maxWidth: '200px',
                                }}
                            >
                                <AddToCartButton product={product.data} promptOnAdd quantity={quantity} buttonStyle={addToCartButtonStyle} labelStyle={addToCartLabelStyle} source="product" />
                            </div>
                        )}
                    </div>
                )}
                {cartBlocked && <BlockedCartNotice />}
            </div>
            <div style={{clear: 'both'}} />
            <CollapsibleDescription
                markdown={product?.data?.description ?? ''}
                className='product-description'
                style={{ margin: '1em 0', ...descriptionStyle }}
                collapsible={!!productConfig.descriptionCollapsible}
                collapsedHeight={productConfig.descriptionCollapsedHeight}
            />
            <ProductImageModal
                open={imageModalOpen}
                onOpenChange={setImageModalOpen}
                images={images}
                index={Math.min(selectedImageIndex, images.length - 1)}
                onIndexChange={setSelectedImageIndex}
                title={product.data?.name}
            />
            {videoUrl && (
                <ProductVideoModal
                    open={videoOpen}
                    onOpenChange={setVideoOpen}
                    videoUrl={videoUrl}
                    title={product.data?.name ?? 'Product Video'}
                />
            )}
            <FrequentlyAddedTogether productId={productId} />
            <MoreLikeThis productId={productId} />
            <LinkedProducts productId={productId} />
        </div>:
        <div className={'centered-content'}>
            <BoxSearchFilled style={{fontSize: '24em', color: '#ddd', paddingBottom: '20px'}} />
            <p><h1>Product Not Found</h1></p>
            <Button
                icon={<HomeFilled/>} 
                style={{width: '25%', marginTop: '1em', maxWidth:'200px'}}
                appearance='primary'
                onClick={() => navigate('/')}
            >
                {L('localization_common_go_home')}
            </Button>
        </div>}
    </div>);
}

export default ProductPage;